import React, { useCallback, useEffect, useRef, useState } from "react";

import {
    Content,
    ContentBody,
    DialogContainer,
    DialogTop,
    DialogTopLeft,
    DialogTopCenter,
    DialogTopRight,
    DialogFooter,
    DialogFooterCenter,
    BtnInsertNewAnswer,
    ContainerForm,
    AvatarContainer,
    AvatarWrapper,
    AvatarOverlay,
    ChangePhotoButton
} from "./style";

import { AiOutlineClose } from "react-icons/ai";
import { FieldProps, Fields } from "../../components/Forms/Fields/FieldBuilder";
import { FormHandles } from "@unform/core";
import FormBuilder from "../../components/Forms/FormBuilder";
import api from "../../services/api";
import { useToast } from "../../hooks/toast";
import { User } from "../../interfaces/User";
import NavigationBar, { NavigationBarProps } from "../../components/NavigationBar";
import { FaCamera } from "react-icons/fa";
import Avatar from "react-avatar";
import UploadImage from "../UploadImage";
import { useAuth } from "../../hooks/auth";

const fieldsForm: FieldProps[] = [
    {
        name: "type",
        type: Fields.COMBO_BOX_FIELD,
        required: true,
        index: 0,
        title: "Permissão",
        description: "Selecione o nível de permissão que este usuário terá no sistema",
        options: [
            { label: "Administrador", value: "A" },
            { label: "Membro", value: "M" },
            { label: "Membro Individual", value: "I" },
            { label: "Solicitante", value: "S" },
            { label: "Visualizador", value: "V" }
        ],
        validation_type: "string",
        validations: [
            {
                type: "required",
                params: ["O campo Tipo de Usuário é obrigatório!"]
            }
        ]
    },
    {
        name: "name",
        type: Fields.TEXT_SHORT_FIELD,
        required: true,
        index: 1,
        title: "Nome Completo",
        description: "Digite o nome completo do usuário",
        validation_type: "string",
        validations: [
            {
                type: "required",
                params: ["O campo Nome do Cadastro é obrigatório!"]
            },
            {
                type: "min",
                params: [2, "O campo Nome deve conter mais de 2 letras!"]
            }
        ],
        max_length: 45
    },
    {
        name: "phone",
        type: Fields.PHONE_FIELD,
        index: 2,
        title: "Telefone",
        description: "Digite o telefone para contato do usuário"
    },
    {
        name: "role_name",
        type: Fields.TEXT_SHORT_FIELD,
        index: 3,
        title: "Função",
        description: "Digite a função deste usuário na empresa",
        max_length: 45
    },
    {
        name: "department",
        type: Fields.TEXT_SHORT_FIELD,
        index: 4,
        title: "Departamento/Setor",
        description: "Digite o departamento ou setor em que este usuário trabalha",
        max_length: 45
    },
    {
        name: "localization",
        type: Fields.TEXT_SHORT_FIELD,
        index: 5,
        title: "Localização",
        description: "Digite o local de trabalho deste usuário",
        max_length: 60
    },
    {
        name: "about_me",
        type: Fields.TEXT_SHORT_FIELD,
        index: 6,
        title: "Sobre",
        description: "Digite informações relevantes sobre este usuário que serão exibidas no perfil"
    },
    {
        name: "color",
        type: Fields.COLOR_PICKER_FIELD,
        index: 7,
        title: "Cor do perfil",
        description: "Escolha uma cor para o perfil deste usuário"
    }
]

const itemsNavBar: NavigationBarProps = {
    items: [
        {
            label: 'Geral',
            id: 1,
            segment: 'active',
            active: true
        }
    ]
}

interface UserConfigProps {
    open: boolean;
    user_id?: number;
    onClose: () => void;
}

const UserConfig: React.FC<UserConfigProps> = ({ onClose, open, user_id }) => {

    const { addToast } = useToast();
    const { user } = useAuth();

    const formRef = useRef<FormHandles>(null);

    const [navBarSelected, setNavBarSelected] = useState(1);
    const [navBarItems] = useState(itemsNavBar.items);
    const [openUploadImage, setOpenUploadImage] = useState(false);

    const [loadingInsert, setLoadingInsert] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [userData, setUserData] = useState<User>();

    const getUserApi = useCallback(async () => {
        setLoading(true);
        api.get('/user', {
            params: { id_user: user_id }
        })
            .then(response => {
                setUserData(response.data);

                const userData = response.data as User;

                // Populate form with user data
                if (formRef.current) {
                    formRef.current.setData(userData);
                }
            })
            .catch(error => {
                addToast({
                    type: 'error',
                    title: 'Erro ao carregar dados',
                    description: 'Não foi possível carregar os dados do usuário.',
                });
            })
            .finally(() => {
                setLoading(false);
            });

    }, [addToast, user_id]);

    const handleClose = useCallback(async () => {
        onClose();
    }, [onClose]);

    const handleSavePage = useCallback(async () => {
        if (formRef.current !== null) {
            setLoadingInsert(true);

            try {
                // Validate form data before submission
                await formRef.current.submitForm();

                // The actual form submission data will be processed in handleSubmitForm
                // so we don't need to duplicate the API call here
            } catch (error) {
                setLoadingInsert(false);
                console.error("Form validation error:", error);
            }
        }
    }, []);

    const handleSubmitForm = useCallback(async (data: any) => {

        if (user_id === user.id_user && data.type !== undefined && data.type !== "A") {
            addToast({
                type: 'error',
                title: 'Erro ao salvar o perfil do usuário',
                description: 'Você não pode alterar seu próprio nível de permissão!',
            });
            return;
        }

        // This function is called when form validation succeeds
        try {
            await api.post('/user', {
                id_user: user_id,
                type: user.id_user === user_id ? undefined : data.type,
                name: data.name,
                phone: data.phone,
                role_name: data.role_name,
                department: data.department,
                about_me: data.about_me,
                localization: data.localization,
                color: data.color
            });

            onClose();
            addToast({
                type: 'success',
                title: 'Perfil atualizado com sucesso!',
                description: 'As configurações do usuário foram atualizadas com sucesso!',
            });
        } catch (error) {
            addToast({
                type: 'error',
                title: 'Erro ao salvar o perfil do usuário',
                description: error.response?.data?.message || 'Ocorreu um erro ao salvar o perfil do usuário!',
            });
        } finally {
            setLoadingInsert(false);
        }
    }, [addToast, user_id, onClose, user.id_user]);

    //Keyboard Shortcuts
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
                handleSavePage();
            }
        }

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleSavePage]);

    useEffect(() => {
        if (open && user_id) {
            getUserApi();
        }
    }, [open, user_id, getUserApi]);

    return (
        <DialogContainer
            fullWidth={true}
            maxWidth="sm"
            open={open}
            onClose={handleClose}
        >
            <DialogTop style={{ border: 'none', paddingBottom: '0px' }}>
                <DialogTopLeft>
                    <h1>
                        {userData !== undefined ? "Configurações de " + userData.name : "Configuração do usuário"}
                    </h1>
                </DialogTopLeft>

                <DialogTopCenter />

                <DialogTopRight>
                    <button onClick={handleClose}><AiOutlineClose /></button>
                </DialogTopRight>
            </DialogTop>
            <DialogTop style={{ marginTop: '5px', paddingBottom: '0px', paddingLeft: '15px' }}>
                <NavigationBar
                    items={navBarItems}
                    navBarSelected={navBarSelected}
                    setNavBarSelected={setNavBarSelected}
                />
            </DialogTop>
            <Content>
                <ContentBody container>

                    {navBarSelected === 1 && (
                        <>
                            {loading ? (
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    width: '100%',
                                    padding: '20px'
                                }}>
                                    <p>Carregando informações do usuário...</p>
                                </div>
                            ) : (
                                <ContainerForm>

                                    <AvatarContainer>
                                        <AvatarWrapper>
                                            <Avatar
                                                className="avatar-user"
                                                src={userData?.avatar_url}
                                                name={userData?.name}
                                                size="100"
                                                round={true}
                                                color={userData?.color}
                                            />
                                            <AvatarOverlay
                                                className="avatar-overlay"
                                                onClick={() => setOpenUploadImage(true)}
                                            >
                                                <FaCamera />
                                            </AvatarOverlay>
                                        </AvatarWrapper>

                                        <ChangePhotoButton onClick={() => setOpenUploadImage(true)}>
                                            <FaCamera />
                                            {userData?.avatar_url ? 'Alterar foto' : 'Adicionar foto'}
                                        </ChangePhotoButton>
                                    </AvatarContainer>

                                    <UploadImage open={openUploadImage} onClose={() => setOpenUploadImage(false)} user_id={user_id} forceUpdate={getUserApi} />

                                    <FormBuilder
                                        id="formFirstStep"
                                        formRef={formRef}
                                        fields={fieldsForm}
                                        handleSubmit={handleSubmitForm}  // This should point to handleSubmitForm, not handleSubmitPage
                                        hideContainer={true}
                                        initialValue={userData}
                                    />
                                </ContainerForm>
                            )}
                        </>
                    )}

                </ContentBody>
            </Content>
            <DialogFooter>
                <DialogFooterCenter>
                    <BtnInsertNewAnswer
                        color={"#9337ed"}
                        style={{ width: '130px', height: '36px' }}
                        onClick={handleSavePage}  // Directly use the function reference here
                        isLoading={loadingInsert || loading}
                        disabled={loading}
                    >
                        Salvar
                    </BtnInsertNewAnswer>
                </DialogFooterCenter>
            </DialogFooter>
        </DialogContainer >
    );

}

export default UserConfig;